import {
  shouldDisplayStudyStartTime,
  STUDY_STATUS,
  checkBiotresOfflineWarning,
  isDisabledViewLiveEcgButton,
  shouldDisplayViewLiveEcgButton,
  checkUsingHighSamplingRateStudy,
  C1cDeviceLabel,
  isManagedC1cDevice,
} from '@/entities/study';
import {
  AlertBanner,
  NormalButton,
  ConfirmModal,
  Chip,
  CustomButton,
  CustomTooltip,
} from '@/shared/ui/components';
import {
  DeviceInfoDrawer,
  checkIcorDevice,
  DEVICE_STATUS,
  AppPaired,
  HighSamplingRate,
  isEndOfLife,
} from '@/entities/device';
import {
  MarkAddressedStudy,
  ConfirmActivityModal,
  SolutionsDrawer,
  ViewLiveEcgModal,
} from '@/pages/studies-list/@x/study-management';
import { editGreenIcon, PendingCommandIcon } from '@/shared/ui/assets';
import { DEVICE, DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import {
  getDuration,
  zeroPad,
  auth,
  formatPhoneNumber,
  dateToString,
  formatInitialDuration,
  getStudyDuration,
  gettextStudyStatus,
  gettextStudyType,
} from '@/shared/lib';
import { QuestionCircleOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import { memo } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useStudyDetailsTab } from './useStudyDetailsTab';
import { EDIT_DRAWER_KEY } from '../../config/editStudyDetails';
import GroupData from './GroupData';
import ManagedEditLock from '../managedEditLock/managedEditLock';
import ActivityLogs from './activityLogs/activityLogs';
import AssignDeviceDrawer from './assignDeviceDrawer/assignDeviceDrawer';
import BiotresOfflineWarning from './biotresOfflineWarning/biotresOfflineWarning';
import EditDiagnosisDrawer from './editStudyDetails/editDiagnosisDrawer/editDiagnosisDrawer';
import EditInterPhysicianDrawer from './editStudyDetails/editInterPhysicianDrawer/editInterPhysicianDrawer';
import EditPhysicianAmendmentNotesDrawer from './editStudyDetails/editPhysicianAmendmentNotesDrawer/editPhysicianAmendmentNotesDrawer';
import EditRefPhysicianDrawer from './editStudyDetails/editRefPhysicianDrawer/editRefPhysicianDrawer';
import './editStudyDetails/editStudyDetails.scss';
import EditStudyInfoDrawer from './editStudyDetails/editStudyInfoDrawer/editStudyInfoDrawer';
import type { DataProps, StudyDetailsTabProps } from './studyDetailsTab.types';
import {
  createTimelineData,
  foStudyContent,
  generateDiagnosis,
  generateHistoryData,
  generateInterpretingPhysician,
  generateReferringPhysician,
  generateStudyIssue,
  renderRowItem,
} from '../../lib/studyDetailsTab';
import './style.scss';

dayjs.extend(utc);

const StudyDetailsTab = ({
  isActive,
  isLoadedData,
  data,
  updateStudyDetail,
  referringPhysicians,
  interprettingPhysicians,
  hasLoginBanner,
  adjustedStudyDayOffset,
  isAbortedStudy,
  isAbortingStudy,
  handleAbortStudy,
  updateSentReferenceCodeStatus,
  handleUpdateStudyAfterAssigningDevice,
  countries,
  handleUpdateReferringPhysicians,
  updateStudyData: updateStudyDataProp,
  studyLogs,
  isC1cProviderStudy = false,
}: StudyDetailsTabProps) => {
  const {
    state,
    setState,
    editDrawer,
    isWebView,
    viewLiveEcgModalRef,
    isPermitEditAmendmentNote,
    isDisableEdit,
    isEndOfDuration,
    hasSentRefCode,
    hasPendingCommandUpdateStudy,
    isStopping,
    isHideMarkAddressedStudy,
    referringPhysicianOptions,
    interprettingPhysicianOptions,
    onCloseDrawer,
    showStudyInfoDrawer,
    toggleDisplaySolutionsDrawer,
    resendReferenceCode,
    toggleDisplayDrawer,
    displayConfirmActivityModal,
    displayStopLiveEcgModal,
    displayViewLiveEcg,
    onClickConfirmActivityModal,
    handleStopStreaming,
    handleResumeStreaming,
    handleAbortClick,
    handleCancelClick,
    handleRightButtonClick,
    handleAssignDeviceClick,
    handleAssignDeviceDrawerClick,
    updateStudyData,
  } = useStudyDetailsTab({
    data,
    referringPhysicians,
    interprettingPhysicians,
    isAbortedStudy,
    isAbortingStudy,
    handleAbortStudy,
    updateSentReferenceCodeStatus,
    handleUpdateStudyAfterAssigningDevice,
    updateStudyData: updateStudyDataProp,
  });

  const isManagedC1cDeviceStudy = isManagedC1cDevice(isC1cProviderStudy, data?.deviceType);

  /**
   * Builds the study information rows of the study details tab: status, initiated and start time, duration, end time, initial duration, patient return visit, device id with the CardioOne device label for LifeSignals and VivaLNK studies, clinician initials, device status, reference code and low cell signal.
   *
   * @remarks
   * The reference code is a bullet, with no send action, and the device id is plain text instead of a drawer link, for a managed LifeSignals or VivaLNK study. Pending command settings override study type, duration and end time, the placeholder device id renders as a bullet, iCOR devices hide status and show a plain id, and a follow-on study appends its tracking block.
   *
   * @param data - Study record supplying status, device, schedule, facility, patient info and pending command.
   * @returns The study information rows plus any follow-on study tracking block.
   */
  const generateStudyInfo = (data: DataProps) => {
    const {
      status,
      device,
      start,
      stop,
      lowCellSignal,
      studyType,
      reportDueDate,
      timezone,
      studyTimeOffset,
      facility,
      initDuration,
      initialDurationOfStudy,
      linkedStudies,
      info,
      referenceCode,
      sentReferenceCodeAt,
      friendlyId,
      ecgDetail,
      patientFirstSyncDeviceAt,
      initiatedTime,
      deviceType,
    } = data;
    const { duration: durationTemp, studyType: studyTypeTemp } =
      data.pendingCommand?.info?.studySetting || {};
    const canNotViewReferenceCodeStudyStatus =
      device?.model === DEVICE.BIOFLUX
        ? [STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE]
        : [STUDY_STATUS.DONE];
    const isFollowOnStudy = !_.isNil(data?.pendingCommand?.info?.studySetting?.followOnStudy)
      ? !_.isEmpty(data?.pendingCommand?.info?.studySetting?.followOnStudy)
      : linkedStudies?.length > 0 || !_.isEmpty(info?.followOnStudy);
    const isDeviceOffline = device?.status === DEVICE_STATUS.OFFLINE;
    const isIcorDevice = checkIcorDevice(data.device);
    const studyInfo: any[] = [
      {
        label: t`Status`,
        value:
          device?.deviceId === '0000000000' && status === STUDY_STATUS.DRAFT
            ? t`Not started`
            : gettextStudyStatus(status),
      },
      {
        label: t`Initiated time`,
        value: initiatedTime
          ? dateToString({
              startStudyDate: start,
              isoDate: initiatedTime,
              timezone,
              studyTimeOffset,
              facilityCountry: facility?.contact?.country,
              facilityTimezone: facility?.timezone,
            })
          : DOUBLE_HYPHEN_BULLET,
      },
      {
        label: t`Study type`,
        value: (
          <div className="study-type">
            <div>{gettextStudyType(studyTypeTemp || studyType)}</div>
            {isFollowOnStudy && (
              <Chip
                style={{
                  marginLeft: 8,
                  border: 1,
                  paddingInline: 8,
                  fontWeight: 500,
                }}
                background="#DEF5EF"
                color="#045C45"
                closable={false}
                className="custom-chip-follow-on"
              >
                {t`Follow-on`}
              </Chip>
            )}
          </div>
        ),
      },
      {
        label: t`Start time`,
        value: shouldDisplayStudyStartTime(status)
          ? start
            ? dateToString({
                startStudyDate: start,
                isoDate: start,
                timezone,
                studyTimeOffset,
                facilityCountry: facility?.contact?.country,
                facilityTimezone: facility?.timezone,
              })
            : DOUBLE_HYPHEN_BULLET
          : DOUBLE_HYPHEN_BULLET,
      },
      {
        label: t`Study duration`,
        value: data?.pendingCommand?.info?.studySetting?.duration
          ? getDuration(data?.pendingCommand?.info?.studySetting?.duration, 'd[d] hh[h] mm[m]', 'h')
          : stop
            ? getDuration(getStudyDuration(start, stop), 'd[d] hh[h] mm[m]', 'h')
            : getDuration(initDuration, 'd[d] hh[h] mm[m]', 'h'),
      },
      {
        label: t`End time`,
        value: durationTemp
          ? dateToString({
              startStudyDate: start,
              isoDate: dayjs(start).add(durationTemp, 'minute'),
              timezone,
              studyTimeOffset,
              facilityCountry: facility?.contact?.country,
              facilityTimezone: facility?.timezone,
            })
          : stop
            ? dateToString({
                startStudyDate: start,
                isoDate: stop,
                timezone,
                studyTimeOffset,
                facilityCountry: facility?.contact?.country,
                facilityTimezone: facility?.timezone,
              })
            : DOUBLE_HYPHEN_BULLET,
      },
      {
        label: t`Initial duration`,
        value: formatInitialDuration(initialDurationOfStudy) || DOUBLE_HYPHEN_BULLET,
      },
      {
        label: t`Patient return visit`,
        value:
          status !== STUDY_STATUS.COMPLETED &&
          status !== STUDY_STATUS.DONE &&
          !isEndOfDuration &&
          info?.followOnStudy?.reportDueDate
            ? dateToString({
                isoDate: dayjs.utc(info?.followOnStudy?.reportDueDate, 'YYYY-MM-DD'),
                timezone: 'UTC+0',
                format: 'MM/DD/YYYY',
              })
            : reportDueDate
              ? dateToString({
                  isoDate: reportDueDate,
                  timezone: 'UTC+0',
                  format: 'MM/DD/YYYY',
                })
              : DOUBLE_HYPHEN_BULLET,
      },
      {
        label: t`Device ID`,
        value:
          device?.deviceId === '0000000000' ? (
            DOUBLE_HYPHEN_BULLET
          ) : (
            <div
              className={classNames(
                'column-gap-2',
                isIcorDevice ? 'icor-device-id-value' : 'device-id-value',
              )}
            >
              {isIcorDevice ? (
                <div className="icor-device-container">
                  <span className="icor-device-id">{device?.deviceId}</span>
                  <span className="icor-device-label">{DEVICE.ICOR}</span>
                </div>
              ) : isManagedC1cDeviceStudy ? (
                <span>{device?.deviceId}</span>
              ) : (
                <NormalButton
                  isText
                  buttonName={device?.deviceId}
                  onClick={() => {
                    toggleDisplayDrawer(data);
                  }}
                />
              )}
              <C1cDeviceLabel deviceType={deviceType} />
              {checkUsingHighSamplingRateStudy(ecgDetail) && <HighSamplingRate />}
              {!!patientFirstSyncDeviceAt && <AppPaired />}
              {isEndOfLife(device?.firstStudyDate) && (
                <Chip background="#FFEBEB" color="#DB1F25">
                  {t`EOL`}
                </Chip>
              )}
            </div>
          ),
      },
      {
        label: t`Clinician initials`,
        value: info?.clinicianInitials || DOUBLE_HYPHEN_BULLET,
      },
      {
        label: t`Device status`,
        value: isIcorDevice ? (
          DOUBLE_HYPHEN_BULLET
        ) : device?.status ? (
          <div className="device-status">
            <Chip
              background={isDeviceOffline ? '#EAEDF1' : '#EDFAF0'}
              color={isDeviceOffline ? '#444F66' : '#07A83D'}
              closable={false}
              className="custom-chip"
            >
              {isDeviceOffline ? t`Offline` : t`Online`}
            </Chip>
          </div>
        ) : (
          DOUBLE_HYPHEN_BULLET
        ),
      },
      {
        label: t`Reference code`,
        value: (
          <div className="reference-code">
            {!_.includes(canNotViewReferenceCodeStudyStatus, status) &&
            !isAbortedStudy &&
            !isManagedC1cDeviceStudy ? (
              <>
                {referenceCode ?? DOUBLE_HYPHEN_BULLET}
                {!!referenceCode &&
                  !!device?.deviceId &&
                  device?.deviceId !== '0000000000' &&
                  status !== STUDY_STATUS.DRAFT && (
                    <span
                      onClick={() => {
                        setState({ isOpenResendModal: true });
                      }}
                      className="resend-btn"
                    >
                      {sentReferenceCodeAt ? t`Resend` : t`Send`}
                    </span>
                  )}
              </>
            ) : (
              DOUBLE_HYPHEN_BULLET
            )}
          </div>
        ),
      },
      {
        label: t`Low cell signal`,
        value: lowCellSignal ? t`Yes` : t`No`,
      },
    ];
    if (isWebView.current) {
      studyInfo.unshift({
        title: t`Study ID`,
        content: zeroPad(friendlyId),
      });
    }
    return (
      <div className="multi-content">
        {studyInfo.map((content) => renderRowItem(content))}
        {(!_.isNil(data?.pendingCommand?.info?.studySetting?.followOnStudy)
          ? !_.isEmpty(data?.pendingCommand?.info?.studySetting?.followOnStudy)
          : data?.linkedStudies?.length > 0 || !_.isEmpty(data?.info?.followOnStudy)) && (
          <div className="f0-study">
            <div className="title">{t`Follow-on study tracking`}</div>
            {foStudyContent(data)}
            {data?.info?.followOnStudy?.cancelledReason === 'RMA' && (
              <AlertBanner
                className="fo-cancelled-alert"
                alertType="warning"
                description={t`The follow-on study was cancelled because the device is under an RMA process.`}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  /**
   * Builds the confirmation question asked before sending the study reference code. For US patients the destination is the formatted primary phone number plus the email when one exists, for other countries only the email, and the verb switches between send and resend depending on whether a code was already sent.
   *
   * @returns The confirmation question naming the reference code destination.
   */
  const generateResendReferenceCodeQuestion = () => {
    let address = null;
    const { info } = data || {};
    const country = info?.patient?.contact?.country;
    const email = info?.patient?.email;
    const phone = info?.patient?.contact?.phone1;
    if (country === 'US') {
      if (email) {
        address = (
          <>
            <b>{formatPhoneNumber(phone)}</b> and <b>{email}</b>
          </>
        );
      } else {
        address = <b>{formatPhoneNumber(phone)}</b>;
      }
    } else {
      address = <b>{email}</b>;
    }
    return (
      <div>
        {`Are you sure you want to ${hasSentRefCode ? 'resend' : 'send'} the reference code to`}{' '}
        {address} ?
      </div>
    );
  };

  /**
   * Conditionally renders the Biotres offline warning on the study details tab. The warning banner is emitted only when the study passes the Biotres offline check.
   *
   * @returns The offline warning banner, or a falsy value when it does not apply.
   */
  const renderBiotresOfflineWarning = () =>
    checkBiotresOfflineWarning((data ?? {}) as any) && <BiotresOfflineWarning />;

  /**
   * Builds the study history block of the study details tab. The raw study history is converted into timeline entries with the last seen time, timezone and study time offset, then rendered as history rows carrying the study start and the facility's country.
   *
   * @returns The rendered study history rows.
   */
  const generateStudyHistory = () => {
    const { studyHistory, lastSeen, timezone, studyTimeOffset } = data || {};
    const result = createTimelineData({
      input: studyHistory || [],
      lastSeen: lastSeen ?? '',
      timezone: timezone ?? '',
      studyTimeOffset: studyTimeOffset ?? 0,
      type: 'studyHistory',
      startStudyDate: data?.start,
      facilityCountry: data?.facility?.contact?.country,
      facilityTimezone: data?.facility?.timezone,
    });
    return generateHistoryData({
      histories: result,
      startStudyDate: data?.start ?? '',
      facilityCountry: data?.facility?.contact?.country ?? '',
      facilityTimezone: data?.facility?.timezone,
      timezone: timezone ?? '',
      studyTimeOffset: studyTimeOffset ?? 0,
    });
  };

  /**
   * Builds the device notification history block of the study details tab. Studies running on an iCOR device, and managed LifeSignals or VivaLNK studies, get an explanatory message instead, otherwise the notifications are turned into timeline entries with the last seen time, timezone and study time offset and then rendered as history rows carrying the study start and facility country.
   *
   * @returns The rendered notification history, or the iCOR or CardioOne unavailability note.
   */
  const generateDeviceNotificationHistory = () => {
    const { deviceNotifications, lastSeen, timezone, studyTimeOffset, device } = data || {};

    const isIcorDevice = checkIcorDevice(device);
    if (isIcorDevice) {
      return (
        <div className="content-description">
          {t`Device notification history are not available for studies running on the iCOR device.`}
        </div>
      );
    }

    if (isManagedC1cDeviceStudy) {
      return (
        <div className="content-description">
          {t`Device notification history is not available for external studies.`}
        </div>
      );
    }

    const result = createTimelineData({
      input: deviceNotifications || [],
      lastSeen: lastSeen ?? '',
      timezone: timezone ?? '',
      studyTimeOffset: studyTimeOffset ?? 0,
      type: 'deviceNotificationHistory',
      startStudyDate: data?.start,
      facilityCountry: data?.facility?.contact?.country,
      facilityTimezone: data?.facility?.timezone,
    });
    return generateHistoryData({
      histories: result,
      startStudyDate: data?.start ?? '',
      facilityCountry: data?.facility?.contact?.country ?? '',
      facilityTimezone: data?.facility?.timezone,
      timezone: timezone ?? '',
      studyTimeOffset: studyTimeOffset ?? 0,
    });
  };

  /**
   * Renders the view live ECG button of the study details tab. Nothing is rendered unless the study qualifies for live ECG, and otherwise the disabled state and tooltip come from the live-ECG eligibility check, with the click opening the live ECG view.
   *
   * @returns The view live ECG button, or null when live ECG is unavailable.
   */
  const viewLiveEcgButtonRenderer = () => {
    if (isManagedC1cDeviceStudy || !shouldDisplayViewLiveEcgButton((data ?? {}) as any)) {
      return null;
    }

    return (
      <CustomButton
        id="view-live-ecg-btn"
        type="outline"
        style="default"
        size="small"
        disabled={isDisabledViewLiveEcgButton((data ?? {}) as any).disabled}
        tooltipTitle={
          isDisabledViewLiveEcgButton((data ?? {}) as any)?.disabled
            ? isDisabledViewLiveEcgButton((data ?? {}) as any)?.disabledText
            : t`View live ECG`
        }
        tooltipPlacement="bottom"
        onClick={() => {
          displayViewLiveEcg(true);
        }}
      >
        {t`View live ECG`}
      </CustomButton>
    );
  };

  /**
   * Renders the abort button of the study details tab. Nothing is rendered for a CardioOne-managed study, while the study is already aborting or when its status is neither draft nor starting, and for Biodirect studies the button is disabled with a contact-support tooltip once an order number or a real assigned device id exists.
   *
   * @returns The abort button, or null when aborting is not offered.
   */
  const abortButtonRenderer = () => {
    if (isC1cProviderStudy || data?.isAborting) {
      return null;
    }

    const canAbortStudyStatuses = [STUDY_STATUS.DRAFT, STUDY_STATUS.STARTING];
    const shouldDisplayAbortButton = canAbortStudyStatuses.includes(data?.status ?? '');
    if (!shouldDisplayAbortButton) {
      return null;
    }

    const isDisabledAbortButton = data?.isBiofluxDirect
      ? data?.orderNumber || (data?.device?.deviceId && data.device.deviceId !== '0000000000')
      : false;
    return (
      <CustomButton
        id={`abort-study-btn-${data?.id}`}
        className={classNames('abort-study-btn', {
          disabled: isDisabledAbortButton,
        })}
        type="outline"
        style="danger"
        size="small"
        onClick={handleAbortClick}
        disabled={!!isDisabledAbortButton}
        tooltipTitle={
          isDisabledAbortButton ? t`Please contact support to abort this study` : undefined
        }
        tooltipPlacement="bottom"
      >
        {t`Abort`}
      </CustomButton>
    );
  };

  /**
   * Renders the assign device button of the study details tab. It appears only while device assignment is pending and is disabled once the study is aborted or aborting.
   *
   * @returns The assign device button, or null when no assignment is pending.
   */
  const assignDeviceButtonRenderer = () => {
    if (!data?.isDeviceAssignmentPending) {
      return null;
    }

    return (
      <CustomButton
        id="assign-device-btn"
        className="assign-device-btn"
        size="small"
        onClick={handleAssignDeviceClick}
        disabled={isAbortedStudy || isAbortingStudy}
      >
        {t`Assign device`}
      </CustomButton>
    );
  };

  /**
   * Groups the left-hand action buttons of the study details tab. The live ECG, abort and assign device renderers are evaluated first and the container is skipped entirely when all three yield nothing.
   *
   * @returns The button container, or null when no action button applies.
   */
  const leftButtonsRenderer = () => {
    const viewLiveEcgButton = viewLiveEcgButtonRenderer();
    const abortButton = abortButtonRenderer();
    const assignDeviceButton = assignDeviceButtonRenderer();
    if (!viewLiveEcgButton && !abortButton && !assignDeviceButton) {
      return null;
    }

    return (
      <div className="left-buttons-container">
        {viewLiveEcgButton}
        {abortButton}
        {assignDeviceButton}
      </div>
    );
  };

  /**
   * Renders a study details section heading with an edit button opening the drawer for that section key. The button is disabled by the tab-level edit lock, the caller's flag or a CardioOne-managed study, and its tooltip shows the aborted-study explanation when the study was aborted, otherwise the supplied tooltip text; a managed study gets the external-portal tooltip instead.
   *
   * @param title - Section heading text.
   * @param editLabel - Accessible label of the edit button.
   * @param key - Study section identifier passed to the drawer opener.
   * @param tooltipText - Tooltip shown when the study is not aborted.
   * @param isDisabled - Extra condition disabling the edit button.
   * @returns The section title with its edit button.
   */
  const renderTitle = (
    title: string,
    editLabel: string,
    key: string,
    tooltipText?: string,
    isDisabled?: boolean,
  ) => (
    <div className="with-edit-icon">
      <div>{title}</div>
      <div className="buttons-container">
        <ManagedEditLock isLocked={isC1cProviderStudy}>
          <CustomButton
            type="secondary"
            size="small"
            disabled={isDisableEdit || isDisabled || isC1cProviderStudy}
            tooltipTitle={
              isAbortedStudy
                ? t`The study has been aborted. You cannot perform any actions.`
                : tooltipText || undefined
            }
            tooltipPlacement="bottomRight"
            icon={<ReactSVG src={editGreenIcon} />}
            ariaLabel={editLabel}
            onClick={() => {
              showStudyInfoDrawer(key);
            }}
          />
        </ManagedEditLock>
      </div>
    </div>
  );

  if (!isActive) {
    return null;
  }

  return (
    <>
      <ConfirmModal
        className="abort-study-confirm-modal"
        isOpen={state.openConfirmAbortStudy}
        title={t`Abort study`}
        question={t`This action cannot be undone. Are you sure you want to abort this study? `}
        leftButtonName="Cancel"
        onClickLeftButton={handleCancelClick}
        rightButtonClassName="abort-btn"
        rightButtonName={t`Abort`}
        buttonRightStyle="danger"
        buttonRightType="primary"
        onClickRightButton={handleRightButtonClick}
        isSaving={state.isCallingAbortStudy}
        destroyOnClose
      />
      <ConfirmModal
        isOpen={state.isOpenTooManyRequestModal}
        rightButtonName={t`Okay`}
        onClickRightButton={() => {
          setState({ isOpenTooManyRequestModal: false });
        }}
        title={t`Resend code limit exceeded`}
        question={state.tooManyRequestMessage}
      />
      <ConfirmModal
        isOpen={state.isOpenResendModal}
        onClickLeftButton={() => {
          setState({ isOpenResendModal: false });
        }}
        onClickRightButton={resendReferenceCode}
        leftButtonName={t`Cancel`}
        rightButtonName={hasSentRefCode ? t`Resend` : t`Send`}
        title={hasSentRefCode ? t`Resend reference code` : t`Send reference code`}
        question={generateResendReferenceCodeQuestion()}
        isSaving={state.isSaving}
      />
      <ConfirmModal
        isOpen={state.openStopLiveEcgModal}
        title={t`Live ECG is stopped`}
        question={t`The live ECG has stopped because you have been inactive for more than 10 minutes.`}
        leftButtonName={t`Close`}
        onClickLeftButton={() => {
          displayStopLiveEcgModal(false);
        }}
        rightButtonName={t`Resume`}
        onClickRightButton={handleResumeStreaming}
        destroyOnClose
      />
      <ConfirmActivityModal
        isOpen={state.openConfirmActivityModal}
        onClickRightButton={onClickConfirmActivityModal}
        handleStopStreaming={handleStopStreaming}
      />
      <ViewLiveEcgModal
        ref={viewLiveEcgModalRef}
        className="view-live-ecg-modal"
        title={t`View live ECG`}
        open={state.openLiveEcgModal}
        study={data}
        onCancel={() => {
          displayViewLiveEcg(false);
        }}
        displayConfirmActivityModal={displayConfirmActivityModal}
      />

      <SolutionsDrawer
        isOpen={state.isOpenSolutionsModal}
        onClickClose={toggleDisplaySolutionsDrawer}
        data={state.displayedSolutionStudy}
      />
      <DeviceInfoDrawer
        visible={state.isVisibleDeviceInfoDrawer}
        deviceId={state.clickedData?.device?.deviceId ?? ''}
        handleClickCloseButton={() => {
          toggleDisplayDrawer();
        }}
      />
      <AssignDeviceDrawer
        visible={state.visibleAssignDeviceDrawer}
        handleClickCloseButton={() => {
          setState({ visibleAssignDeviceDrawer: false });
        }}
        study={data as any}
        handleAssignDeviceDrawerClick={handleAssignDeviceDrawerClick}
      />

      <div
        className={classNames('study-details-tab animated fadeIn', {
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
          'has-login-banner': hasLoginBanner,
        })}
      >
        {data?.facility?.canViewFlaggedStudies && data?.flaggedStudy?.reason && (
          <GroupData
            className="group-issue"
            isLoadedData={isLoadedData}
            title={
              <div className="with-edit-icon">
                <div>{t`Study's issue`}</div>
                <div className="resolved">
                  <MarkAddressedStudy
                    lastAddressedTime={data.lastAddressedTime}
                    studyId={data.id}
                    resolvedAt={data?.flaggedStudy?.resolvedAt}
                    isHideMarkAddressedStudy={isHideMarkAddressedStudy}
                  />
                </div>
              </div>
            }
            content={generateStudyIssue(
              data,
              toggleDisplaySolutionsDrawer,
              isHideMarkAddressedStudy,
            )}
          />
        )}
        <GroupData
          isLoadedData={isLoadedData}
          className="group-data"
          title={
            <>
              <div className="with-edit-icon">
                <div>{t`Study information`}</div>
                <div className="buttons-container">
                  {leftButtonsRenderer()}
                  <ManagedEditLock isLocked={isC1cProviderStudy}>
                    <CustomButton
                      type="secondary"
                      size="small"
                      disabled={isDisableEdit || isC1cProviderStudy}
                      tooltipTitle={
                        isAbortedStudy
                          ? t`The study has been aborted. You cannot perform any actions.`
                          : undefined
                      }
                      tooltipPlacement="bottomRight"
                      icon={<ReactSVG src={editGreenIcon} />}
                      ariaLabel={t`Edit study information`}
                      onClick={() => {
                        showStudyInfoDrawer(EDIT_DRAWER_KEY.STUDY_INFO);
                      }}
                    />
                  </ManagedEditLock>
                </div>
              </div>
              {renderBiotresOfflineWarning()}
              {hasPendingCommandUpdateStudy && (
                <div className="pending-command__container">
                  <img
                    className="pending-command__icon"
                    src={PendingCommandIcon}
                    alt="Pending command icon"
                  />
                  <span className="pending-command__message">
                    {data?.device?.status === DEVICE_STATUS.OFFLINE
                      ? t`Waiting for the device to come back online to apply study information changes. Updates are temporarily disabled during this time.`
                      : t`Study Information changes are being applied, please hold.`}
                  </span>
                </div>
              )}
            </>
          }
          content={generateStudyInfo(data ?? ({} as DataProps))}
        />
        <div className="small-group-data">
          <GroupData
            isLoadedData={isLoadedData}
            title={renderTitle(
              t`Referring physician`,
              t`Edit referring physician`,
              EDIT_DRAWER_KEY.REFERRING_PHYSICIAN,
            )}
            content={generateReferringPhysician(data)}
          />
          <GroupData
            isLoadedData={isLoadedData}
            title={renderTitle(
              t`Interpreting physician`,
              t`Edit interpreting physician`,
              EDIT_DRAWER_KEY.INTEREPTING_PHYSICIAN,
            )}
            content={generateInterpretingPhysician(data)}
          />
          <GroupData
            isLoadedData={isLoadedData}
            title={renderTitle(t`Diagnosis`, t`Edit diagnosis`, EDIT_DRAWER_KEY.DIAGNOSIS)}
            content={generateDiagnosis(data)}
          />
          <GroupData
            isLoadedData={isLoadedData}
            title={renderTitle(
              t`Physician amendment notes`,
              t`Edit physician amendment notes`,
              EDIT_DRAWER_KEY.PHYSICIAN_NOTES,
              !auth.isClinicPhysician()
                ? t`This information can only be edited by a Clinic Physician and requires a physician's signature.`
                : !isPermitEditAmendmentNote
                  ? t`Only the assigned Interpreting Physician can edit this field.`
                  : undefined,
              !auth.isClinicPhysician() || !isPermitEditAmendmentNote,
            )}
            content={<div className="physician-notes">{data?.info?.diagnosis?.physiciansNote}</div>}
          />
          <GroupData
            className="history-tracking"
            isLoadedData={isLoadedData}
            title={t`Study history`}
            content={generateStudyHistory()}
          />
          <GroupData
            className="history-tracking"
            isLoadedData={isLoadedData}
            title={t`Device notification history`}
            content={generateDeviceNotificationHistory()}
          />
          {!_.isEmpty(studyLogs) && (
            <GroupData
              className="history-tracking"
              isLoadedData={isLoadedData}
              title={
                <div className="title-with-icon">
                  {t`Activity logs`}
                  <CustomTooltip
                    placement="bottom"
                    arrow={false}
                    title={t`Activity logs provide a detailed timeline of user actions for the study.`}
                  >
                    <QuestionCircleOutlined />
                  </CustomTooltip>
                </div>
              }
              content={
                <ActivityLogs
                  startStudyDate={data?.start}
                  timezone={data?.timezone}
                  studyTimeOffset={data?.studyTimeOffset}
                  facilityCountry={data?.facility?.contact?.country}
                  facilityTimezone={data?.facility?.timezone}
                  studyLogs={studyLogs}
                />
              }
            />
          )}
        </div>

        {!_.isEmpty(data) && isLoadedData && (
          <>
            <EditStudyInfoDrawer
              data={data}
              open={editDrawer[EDIT_DRAWER_KEY.STUDY_INFO]}
              adjustedStudyDayOffset={adjustedStudyDayOffset}
              onClose={onCloseDrawer}
              drawerKey={EDIT_DRAWER_KEY.STUDY_INFO}
              updateStudyDetail={() => {
                onCloseDrawer(EDIT_DRAWER_KEY.STUDY_INFO);
                updateStudyDetail();
              }}
              width={500}
              isStopping={isStopping}
            />

            <EditRefPhysicianDrawer
              data={data}
              open={editDrawer[EDIT_DRAWER_KEY.REFERRING_PHYSICIAN]}
              referringPhysicians={referringPhysicianOptions}
              countries={countries}
              handleUpdateReferringPhysicians={handleUpdateReferringPhysicians}
              onClose={onCloseDrawer}
              drawerKey={EDIT_DRAWER_KEY.REFERRING_PHYSICIAN}
              updateStudyData={(...rest) => updateStudyData(...rest, true)}
              width={500}
            />

            <EditInterPhysicianDrawer
              data={data}
              open={editDrawer[EDIT_DRAWER_KEY.INTEREPTING_PHYSICIAN]}
              interprettingPhysicians={interprettingPhysicianOptions}
              onClose={onCloseDrawer}
              drawerKey={EDIT_DRAWER_KEY.INTEREPTING_PHYSICIAN}
              updateStudyData={(...rest) => updateStudyData(...rest, true)}
              width={500}
            />

            <EditDiagnosisDrawer
              data={data}
              open={editDrawer[EDIT_DRAWER_KEY.DIAGNOSIS]}
              onClose={onCloseDrawer}
              drawerKey={EDIT_DRAWER_KEY.DIAGNOSIS}
              updateStudyData={(...rest) => updateStudyData(...rest, true)}
              width={500}
            />

            <EditPhysicianAmendmentNotesDrawer
              data={data}
              open={editDrawer[EDIT_DRAWER_KEY.PHYSICIAN_NOTES]}
              onClose={onCloseDrawer}
              drawerKey={EDIT_DRAWER_KEY.PHYSICIAN_NOTES}
              updateStudyData={updateStudyData}
              width={500}
            />
          </>
        )}
      </div>
    </>
  );
};

export default memo(StudyDetailsTab);
